import React, { useState } from 'react';
import { PolicyPage, Section, Callout, PolicyTable } from './PolicyLayout';
import { GOLD, GOLD_DIM, GOLD_MID } from './PolicyLayout';

const RELATED = [
  { label: 'Privacy Policy',      path: '/privacy'      },
  { label: 'Terms of Service',    path: '/terms'        },
  { label: 'Cookie Policy',       path: '/cookies'      },
  { label: 'Cancellation Policy', path: '/cancellation' },
];

const SECTIONS = [
  { id: 'what',        label: 'What Are Cookies'    },
  { id: 'types',       label: 'Cookie Categories'   },
  { id: 'third-party', label: 'Third-Party Cookies' },
  { id: 'list',        label: 'Cookies We Use'      },
  { id: 'control',     label: 'Managing Cookies'    },
  { id: 'consent',     label: 'Your Consent'        },
  { id: 'updates',     label: 'Policy Updates'      },
  { id: 'contact',     label: 'Contact'             },
];

/* ── Cookie toggle — mobile-first ──
   Stack on small screens: text top, toggle bottom-right.
   On wider screens: side-by-side as before.                */
const CookieToggle = ({ name, description, required, defaultOn }) => {
  const [on, setOn] = useState(defaultOn);
  const active = required || on;

  return (
    <div style={{
      padding: '18px 0',
      borderBottom: '1px solid #f0ece6',
    }}>
      {/* Row: name + badge + toggle */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        marginBottom: '6px',
        flexWrap: 'nowrap', /* keep toggle on same line as name */
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flexShrink: 1 }}>
          <span className="f-jost" style={{
            fontSize: 'clamp(14px,2.5vw,16px)', fontWeight: 500,
            color: '#12100e', whiteSpace: 'nowrap', overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {name}
          </span>
          {required && (
            <span className="f-jost" style={{
              fontSize: '12px', fontWeight: 600, letterSpacing: '0.14em',
              textTransform: 'uppercase', padding: '2px 7px', flexShrink: 0,
              border: `1px solid ${GOLD_MID}`, color: GOLD, background: GOLD_DIM,
              whiteSpace: 'nowrap',
            }}>
              Required
            </span>
          )}
        </div>

        {/* Toggle switch — fixed size, never shrinks */}
        <button
          onClick={() => !required && setOn(v => !v)}
          aria-label={`${active ? 'Disable' : 'Enable'} ${name} cookies`}
          aria-checked={active}
          role="switch"
          style={{
            flexShrink: 0,
            width: '44px', height: '24px',
            borderRadius: '12px',
            background: active ? GOLD : '#e2e8f0',
            border: 'none',
            cursor: required ? 'not-allowed' : 'pointer',
            position: 'relative',
            transition: 'background .3s',
            outline: 'none',
          }}
        >
          <span style={{
            position: 'absolute', top: '3px',
            left: active ? 'calc(100% - 21px)' : '3px',
            width: '18px', height: '18px',
            borderRadius: '50%', background: '#fff',
            transition: 'left .3s cubic-bezier(0.22,1,0.36,1)',
            boxShadow: '0 1px 3px rgba(0,0,0,.2)',
          }} />
        </button>
      </div>

      {/* Description below — full width, no competition with toggle */}
      <p className="f-jost" style={{
        fontSize: 'clamp(13px,2vw,15px)', fontWeight: 300,
        color: '#12100e', lineHeight: 1.65, margin: 0,
      }}>
        {description}
      </p>
    </div>
  );
};

/* ── Cookie code token ── */
const Code = ({ children }) => (
  <code style={{ fontFamily: 'monospace', fontSize: '15px', color: GOLD }}>{children}</code>
);

const CookiePolicy = () => (
  <PolicyPage
    badge="Legal · Cookies"
    title="Cookie Policy"
    subtitle="We use cookies to give you the best possible experience on our platform. This policy explains exactly what cookies we use, why we use them, and how you can control them."
    updated="January 15, 2026"
    heroImage="https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=1400&q=80"
    sections={SECTIONS}
    relatedPages={RELATED}
    currentPage="/cookies"
  >

    <Section id="what" label="The Basics" title="What Are Cookies?">
      <p className="policy-p">
        Cookies are small text files placed on your device when you visit a website. They serve many functions: keeping you logged in between visits, remembering your preferences, understanding how you use our site, and enabling us to improve your experience over time.
      </p>
      <p className="policy-p">
        Cookies are not programs and cannot access files on your device or execute code. They are simply small pieces of data stored in your browser that websites can read back.
      </p>
      <Callout>
        <strong>Your control:</strong> You can manage, restrict, or delete cookies at any time through your browser settings or our cookie preference centre below. Required cookies cannot be disabled as they are essential for the site to function.
      </Callout>
    </Section>

    <div className="section-divider" />

    <Section id="types" label="Categories" title="Cookie Categories">
      <p className="policy-p">
        We use four categories of cookies. Use the toggles below to manage your preferences — your choices are saved instantly.
      </p>

      <div style={{
        border: '1px solid #e8e4de', background: '#fff',
        padding: 'clamp(16px,4vw,24px) clamp(16px,4vw,28px)',
        marginTop: '24px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          height: '2px',
          background: `linear-gradient(90deg, ${GOLD}, rgba(184,151,90,0.1))`,
          position: 'absolute', top: 0, left: 0, right: 0,
        }} />

        <CookieToggle
          name="Strictly Necessary"
          description="Essential for the website to function. These enable core features such as authentication, booking sessions, payment security, and remembering your cart. Cannot be disabled."
          required={true}
          defaultOn={true}
        />
        <CookieToggle
          name="Performance & Analytics"
          description="Help us understand how visitors interact with our website. All data is aggregated and anonymous — we use this to improve page speed, navigation, and content."
          required={false}
          defaultOn={true}
        />
        <CookieToggle
          name="Functional"
          description="Remember your preferences — language, currency, previously viewed tours, and form data — to give you a more personalised experience."
          required={false}
          defaultOn={true}
        />
        <CookieToggle
          name="Marketing & Targeting"
          description="Used to show you relevant advertisements on third-party platforms. We never share your data for advertising purposes without your consent."
          required={false}
          defaultOn={false}
        />
      </div>
    </Section>

    <div className="section-divider" />

    <Section id="third-party" label="Third Parties" title="Third-Party Cookies">
      <p className="policy-p">
        Some cookies on our site are set by trusted third-party services that help us deliver our platform. These services have their own privacy and cookie policies.
      </p>

      {/* PolicyTable adds horizontal scroll + visual hint on mobile */}
      <PolicyTable>
        <thead>
          <tr>
            <th>Provider</th>
            <th>Purpose</th>
            <th>Category</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Stripe',           'Secure payment processing, fraud prevention',              'Necessary'  ],
            ['Google Analytics', 'Anonymised usage statistics and performance monitoring',   'Performance'],
            ['Hotjar',           'Heatmaps and session recordings (anonymised)',             'Performance'],
            ['Intercom',         'Live chat and customer support widget',                    'Functional' ],
            ['Meta Pixel',       'Conversion tracking and retargeting (with consent only)',  'Marketing'  ],
            ['Google Ads',       'Ad performance measurement (with consent only)',           'Marketing'  ],
          ].map(([provider, purpose, category], i) => (
            <tr key={i} style={{ background: i % 2 === 1 ? '#faf9f7' : '#fff' }}>
              <td><strong>{provider}</strong></td>
              <td>{purpose}</td>
              <td>{category}</td>
            </tr>
          ))}
        </tbody>
      </PolicyTable>

      <p className="policy-p">
        Marketing cookies are only activated if you have given explicit consent via our cookie preference centre. You can withdraw consent at any time.
      </p>
    </Section>

    <div className="section-divider" />

    <Section id="list" label="Full Cookie List" title="Every Cookie We Use">
      <p className="policy-p">
        In the interest of complete transparency, here is every cookie currently set by our platform and its specific purpose.
      </p>

      <PolicyTable>
        <thead>
          <tr>
            <th>Cookie Name</th>
            <th>Provider</th>
            <th>Duration</th>
            <th>Purpose</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['_session',          'voleluxe.co.tz', 'Session',    'Maintains login state'],
            ['_csrf',             'voleluxe.co.tz', 'Session',    'Security — prevents cross-site request forgery'],
            ['_booking_cart',     'voleluxe.co.tz', '7 days',     'Remembers items in your booking cart'],
            ['_cookie_prefs',     'voleluxe.co.tz', '1 year',     'Stores your cookie consent preferences'],
            ['_ga',               'Google',        '2 years',    'Analytics — distinguishes users (anonymised ID)'],
            ['_gid',              'Google',        '24 hours',   'Analytics — distinguishes users within a session'],
            ['_hjSession',        'Hotjar',        '30 minutes', 'Session recording for UX improvement (anonymised)'],
            ['intercom-session',  'Intercom',      '1 week',     'Live chat session continuity'],
            ['_fbp',              'Meta',          '90 days',    'Marketing — only set with consent'],
          ].map(([name, provider, duration, purpose], i) => (
            <tr key={i} style={{ background: i % 2 === 1 ? '#faf9f7' : '#fff' }}>
              <td><Code>{name}</Code></td>
              <td>{provider}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{duration}</td>
              <td>{purpose}</td>
            </tr>
          ))}
        </tbody>
      </PolicyTable>
    </Section>

    <div className="section-divider" />

    <Section id="control" label="Your Control" title="Managing & Deleting Cookies">
      <p className="policy-p">
        You have several ways to manage cookies:
      </p>
      <ul className="policy-ul">
        <li><strong>Cookie preference centre:</strong> Use the toggles in the "Cookie Categories" section above to manage non-essential cookies at any time</li>
        <li><strong>Browser settings:</strong> All modern browsers allow you to block or delete cookies. See your browser's help section for instructions — note this may impact site functionality</li>
        <li><strong>Google Analytics opt-out:</strong> Install the <a href="https://tools.google.com/dlpage/gaoptout" style={{ color: GOLD }}>Google Analytics Opt-out Browser Add-on</a></li>
        <li><strong>Your Online Choices:</strong> Visit <a href="https://www.youronlinechoices.com" style={{ color: GOLD }}>youronlinechoices.com</a> to manage interest-based advertising across multiple providers</li>
      </ul>
      <Callout>
        Disabling strictly necessary cookies will prevent our website from functioning correctly. Booking, payment, and account features will not work without them.
      </Callout>
    </Section>

    <div className="section-divider" />

    <Section id="consent" label="Your Consent" title="How We Obtain & Record Consent">
      <p className="policy-p">
        When you first visit our website, we display a cookie consent banner that allows you to accept all cookies, reject non-essential cookies, or customise your preferences. Your choice is recorded in a consent cookie (<Code>_cookie_prefs</Code>) and honoured across all sessions on your device.
      </p>
      <p className="policy-p">
        We log the following for compliance purposes: the date and time of consent, the version of our cookie policy that was in effect, and the choices made. This log is retained for 3 years.
      </p>
      <p className="policy-p">
        You may withdraw consent or change your preferences at any time using the cookie preference centre above. Withdrawal of consent does not affect the lawfulness of any processing that occurred before you withdrew.
      </p>
    </Section>

    <div className="section-divider" />

    <Section id="updates" label="Policy Updates" title="Changes to This Policy">
      <p className="policy-p">
        We review and update this Cookie Policy at least annually, and whenever we make material changes to our use of cookies. We will notify you of significant changes via our consent banner and, where appropriate, by email.
      </p>
      <p className="policy-p">
        The date at the top of this page reflects when the policy was last updated. We recommend reviewing this page periodically to stay informed of any changes.
      </p>
    </Section>

    <div className="section-divider" />

    <Section id="contact" label="Questions" title="Questions About Cookies">
      <p className="policy-p">
        If you have any questions about our use of cookies or this policy, please contact our Privacy Team.
      </p>
      <ul className="policy-ul">
        <li><strong>Email:</strong> privacy@voleluxe.co.tz</li>
        <li><strong>Subject line:</strong> "Cookie Policy Enquiry"</li>
        <li><strong>Response time:</strong> Within 2 business days</li>
      </ul>
    </Section>

  </PolicyPage>
);

export default CookiePolicy;